import React, { useEffect, useMemo, useState } from "react";

import {
  ANDROID_PLAY_STORE_URL,
  IOS_APP_STORE_URL,
  IOS_APP_UNIVERSAL_LINK,
} from "../../utils/constants/appLinks";
import { isIOS, isIOSSafari } from "../../utils/helper/helper";

const safelyReadStorage = (storage, key) => {
  try {
    return storage?.getItem(key);
  } catch (_) {
    return null;
  }
};

const isRunningInsideNativeApp = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const query = window.location?.search || "";
  const params = new URLSearchParams(query);
  const userAgent = window.navigator?.userAgent || "";

  return Boolean(
    window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
      window.IS_APP_WEBVIEW === true ||
      userAgent.includes("OATRxDeliveryAppWebView") ||
      query.includes("flutter_app=1") ||
      params.get("source") === "app_webview" ||
      safelyReadStorage(window.sessionStorage, "flutter_app") === "1" ||
      window.navigator?.standalone ||
      window.matchMedia?.("(display-mode: standalone)")?.matches,
  );
};

const isAndroid = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return /Android/i.test(window.navigator?.userAgent || "");
};

const IosAppDownloadPrompt = () => {
  const [isVisible, setIsVisible] = useState(false);

  const platform = useMemo(() => {
    if (typeof window === "undefined") {
      return null;
    }

    if (isRunningInsideNativeApp()) {
      return null;
    }

    if (isIOS() && isIOSSafari()) {
      return "ios-safari";
    }

    if (isIOS()) {
      return "ios";
    }

    if (isAndroid()) {
      return "android";
    }

    return null;
  }, []);

  useEffect(() => {
    setIsVisible(Boolean(platform));
  }, [platform]);

  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (!isVisible || !platform) {
    return null;
  }

  const isAndroidPlatform = platform === "android";
  const openUrl = isAndroidPlatform ? ANDROID_PLAY_STORE_URL : IOS_APP_UNIVERSAL_LINK || IOS_APP_STORE_URL;
  const title = "Open Medidoze Delivery";
  const ariaLabel = isAndroidPlatform ? "Open Android app" : "Open iOS app";
  const dismissLabel = isAndroidPlatform ? "Dismiss Android app prompt" : "Dismiss iOS app prompt";
  const actionLabel = isAndroidPlatform || platform === "ios-safari" ? "View" : "Open";
  const platformIcon = isAndroidPlatform ? (
    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M6.52 5.46 4.9 3.84a.58.58 0 0 1 0-.82.58.58 0 0 1 .82 0l1.72 1.72A8.1 8.1 0 0 1 12 3.38c1.66 0 3.2.5 4.48 1.36l1.72-1.72a.58.58 0 0 1 .82 0 .58.58 0 0 1 0 .82L17.4 5.46A7.3 7.3 0 0 1 19.37 10H4.63A7.3 7.3 0 0 1 6.52 5.46ZM8.75 8.13a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm6.5 0a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM4.62 11.25h14.76v7.88c0 .82-.67 1.5-1.5 1.5H6.13c-.83 0-1.5-.68-1.5-1.5v-7.88Z" />
    </svg>
  ) : (
    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.05 12.48c-.03-2.14 1.76-3.17 1.84-3.22-1.01-1.48-2.58-1.68-3.13-1.7-1.32-.14-2.6.79-3.27.79-.68 0-1.71-.77-2.82-.75-1.44.02-2.78.86-3.52 2.18-1.52 2.63-.39 6.5 1.07 8.63.73 1.05 1.58 2.22 2.69 2.18 1.09-.04 1.5-.7 2.81-.7 1.3 0 1.68.7 2.82.67 1.17-.02 1.9-1.05 2.6-2.11.84-1.2 1.17-2.39 1.18-2.45-.03-.01-2.24-.86-2.27-3.52zM14.9 6.16c.59-.73.99-1.71.88-2.71-.85.04-1.92.59-2.54 1.3-.55.63-1.04 1.65-.91 2.62.96.07 1.95-.49 2.57-1.21z" />
    </svg>
  );

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9999] px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
      role="region"
      aria-label={ariaLabel}
    >
      <div
        className="mx-auto flex w-full max-w-md items-start gap-2 rounded-[15px] border border-[#ececec] bg-white px-2.5 py-2"
        style={{ boxShadow: "0 10px 36px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.08)" }}
      >
        <div className="shrink-0 overflow-hidden rounded-xl" aria-hidden>
          <img
            src={`${process.env.PUBLIC_URL || ""}/favicon.png`}
            alt=""
            width={44}
            height={44}
            className="h-11 w-11 object-cover"
            decoding="async"
          />
        </div>

        <div className="min-w-0 flex-1 pr-0.5 pt-0.5">
          <p className="text-[13px] font-bold leading-tight text-black">
            {title}
          </p>
          <p className="mt-0.5 text-[11px] font-normal leading-snug text-[#666666]">
            Continue in the app for faster access and smoother performance.
          </p>
        </div>

        <div className="ml-auto flex shrink-0 flex-col items-end gap-1.5">
          <button
            type="button"
            onClick={handleDismiss}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[#e5e5e5] bg-white text-[#b3b3b3] transition hover:border-[#d4d4d4] hover:text-[#737373] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d4d4d4]"
            aria-label={dismissLabel}
          >
            <svg
              className="h-3 w-3"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              aria-hidden
            >
              <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
            </svg>
          </button>
          {platform === "ios-safari" ? (
            <a
              href={IOS_APP_STORE_URL}
              className="inline-flex items-center justify-center gap-1 rounded-lg bg-blue-700 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300"
              rel="noopener noreferrer"
            >
              {platformIcon}
              {actionLabel}
            </a>
          ) : openUrl ? (
            <a
              href={openUrl}
              className="inline-flex items-center justify-center gap-1 rounded-lg bg-blue-700 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300"
              rel="noopener noreferrer"
            >
              {platformIcon}
              View
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default IosAppDownloadPrompt;
